import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight, Check, Edit3, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BodyReader, prepareBody } from "./body-reader";
import { AgenticChips, PublishStatusPanel, VetoControls } from "./agentic-ui";
import { isNeedsFix, publishAfterOf, vetoStateOf } from "./agentic";
import { ChecksPanel, useMinWidth } from "./checks-panel";
import { countSources } from "./html";
import { ledgerStageSummary } from "./ledger-view";
import { QualityReview } from "./quality-review";
import { ledgerOf, qaReviewOf, scoreCardOf } from "./quality";
import { Chip, LiveLink, OriginChip, PublishActions, QaChip, StageChip, TYPE_CHIP } from "./item-ui";
import {
  agentLabel,
  deriveTitle,
  fmtDate,
  fmtDateTime,
  humanizeCheckName,
  isApproved,
  isPending,
  isPublished,
  isRejected,
  kindOf,
  metaDescriptionOf,
  parseQa,
  rejectionReasonOf,
  sourcesCountFromContext,
  stageOf,
  toDate,
  typeLabel,
  whyNowOf,
  type StudioItem,
} from "./helpers";
import { useContentItem, useStudioActions } from "./use-studio";

const SIDE_BY_SIDE_MIN_WIDTH = 1200;

export interface ReviewViewProps {
  itemId: number;
  /** Row from the list, used immediately while the full row loads. */
  listItem: StudioItem | undefined;
  workspace: any;
  onBack: () => void;
  /** Where "Next" goes, plus a "3 of 11" position, when the owner came from the inbox. */
  next?: { id: number; position: number; total: number } | null;
  onOpenItem: (id: number) => void;
  /** Opens the existing legacy editor (Blog editor for articles, content editor for the rest). */
  renderEditor: (item: StudioItem, onClose: () => void) => ReactNode;
  backLabel: string;
}

export function ReviewView({ itemId, listItem, workspace, onBack, next, onOpenItem, renderEditor, backLabel }: ReviewViewProps) {
  const { item, isLoading } = useContentItem(itemId, listItem);
  const { approve, reject } = useStudioActions();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [editing, setEditing] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Checks sit beside the article only when the main column is wide enough to keep the article readable
  // (the app sidebar takes ~240px of the window); below that they stack above it, collapsed.
  const sideBySide = useMinWidth(SIDE_BY_SIDE_MIN_WIDTH);

  useEffect(() => {
    topRef.current?.scrollIntoView({ block: "start" });
    setRejecting(false);
    setReason("");
    setEditing(false);
  }, [itemId]);

  const title = useMemo(() => (item ? deriveTitle(item) : ""), [item]);
  const prepared = useMemo(() => (item ? prepareBody(item, title) : null), [item, title]);

  if (isLoading && !item) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground" data-testid="review-loading">
        <Loader2 className="mr-2 animate-spin" size={18} /> Opening…
      </div>
    );
  }

  if (!item || !prepared) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 py-10 text-center" data-testid="review-missing">
        <p className="text-base font-semibold text-foreground">This item isn't available.</p>
        <p className="text-sm text-muted-foreground">It may have been removed, or it belongs to a different workspace.</p>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft size={14} /> {backLabel}
        </Button>
      </div>
    );
  }

  const kind = kindOf(item);
  const stage = stageOf(item);
  const qa = parseQa(item);
  // The item's own ledger (sourceContext.ledger) first; the comment inside the article body only when it has none.
  const ledger = ledgerOf(item, prepared.article?.sources ?? null);
  const sourcesCount = (ledger ? countSources(ledger.text) : null) ?? prepared.article?.sourcesCount ?? sourcesCountFromContext(item);
  const hasSourcesPanel = !!ledger;
  const ledgerNotInForce = ledger ? ledgerStageSummary(ledger.text).notInForce : 0;
  const graded = scoreCardOf(item).present || !!qaReviewOf(item);
  const scheduled = toDate(item.scheduledAt);
  const published = isPublished(item);
  const veto = vetoStateOf(item);
  const goesOut = veto === "pending" ? publishAfterOf(item) : null;
  const whenText = published
    ? `Published ${fmtDateTime(item.publishedAt || item.scheduledAt) || ""}`.trim()
    : goesOut
      ? "Publishes by itself unless held"
      : veto === "held"
        ? "On hold"
        : veto === "released"
          ? "Released"
          : scheduled
            ? stage === "overdue"
              ? `Was due ${fmtDateTime(scheduled)} (not sent)`
              : `Scheduled for ${fmtDateTime(scheduled)}`
            : "Not scheduled";
  const whyNow = whyNowOf(item);
  const metaDescription = metaDescriptionOf(item);
  const rejectedReason = isRejected(item) ? rejectionReasonOf(item) : null;
  const busy = approve.isPending || reject.isPending;
  // A piece waiting out its veto window (or held) is decided with Hold / Release, not Approve.
  const needsFix = isNeedsFix(item);
  const vetoActive = !published && !needsFix && !isRejected(item) && ["pending", "held", "released"].includes(vetoStateOf(item));
  const autoManaged = !published && vetoStateOf(item) !== "none";

  // "See list" in the Checks panel: open the Sources panel under the article and bring it into view.
  const seeSources = () => {
    const el = bodyRef.current?.querySelector<HTMLDetailsElement>('[data-testid="panel-sources"]');
    if (!el) return;
    el.open = true;
    el.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  const submitReject = () => {
    reject.mutate(
      { id: item.id, reason: reason.trim() },
      {
        onSuccess: () => {
          setRejecting(false);
          setReason("");
          onBack();
        },
      },
    );
  };

  const decision = (placement: "top" | "bottom") => (
    <div className="flex flex-wrap items-start gap-2" data-testid={`decision-${placement}`}>
      {vetoActive && <VetoControls item={item} testIdSuffix={`-${placement}`} />}
      {(isPending(item) || vetoActive || (needsFix && !isRejected(item))) && (
        <>
          {!vetoActive && !needsFix && (
            <Button
              type="button"
              disabled={busy}
              onClick={() => approve.mutate(item.id)}
              className="bg-emerald-600 text-white hover:bg-emerald-600/90"
              data-testid={`button-approve-${placement}`}
            >
              {approve.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Approve
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => {
              setRejecting(true);
              if (placement === "bottom") topRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
            }}
            data-testid={`button-reject-${placement}`}
          >
            <X size={14} /> Reject
          </Button>
        </>
      )}
      <Button type="button" variant="outline" onClick={() => setEditing(true)} data-testid={`button-edit-${placement}`}>
        <Edit3 size={14} /> Edit
      </Button>
    </div>
  );

  return (
    <div ref={topRef} className="mx-auto w-full max-w-6xl space-y-4" data-testid="review-view">
      {/* Back / next */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onBack} data-testid="button-review-back">
          <ArrowLeft size={14} /> {backLabel}
        </Button>
        {next && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenItem(next.id)} data-testid="button-review-next">
            Next item ({next.position} of {next.total}) <ArrowRight size={14} />
          </Button>
        )}
      </div>

      {/* Decision header */}
      <section className="space-y-4 rounded-2xl border border-border bg-card/70 p-4 sm:p-5" data-testid="decision-header">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip className={TYPE_CHIP}>{typeLabel(item)}</Chip>
            <OriginChip item={item} />
            <StageChip item={item} />
            <QaChip item={item} />
            <AgenticChips item={item} />
          </div>
          <h1 className="break-words text-xl font-bold leading-snug text-foreground sm:text-2xl" data-testid="review-title">
            {title}
          </h1>
        </div>

        <p className="text-sm text-muted-foreground" data-testid="review-meta">
          <span data-testid="meta-agent">{agentLabel(item)}</span>
          <span aria-hidden> · </span>
          <span data-testid="meta-created">{fmtDate(item.createdAt) || "—"}</span>
          <span aria-hidden> · </span>
          <span data-testid="fact-when">{whenText}</span>
        </p>

        {qa && qa.failing.length > 0 && (
          <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200" data-testid="qa-failing">
            Did not pass: {qa.failing.slice(0, 6).map(humanizeCheckName).join(", ")}
            {qa.failing.length > 6 ? ` and ${qa.failing.length - 6} more` : ""}
          </p>
        )}

        <PublishStatusPanel item={item} workspace={workspace} />

        {whyNow && (
          <p className="text-sm text-foreground/90" data-testid="why-now">
            <span className="font-medium text-foreground">Why now: </span>
            {whyNow}
          </p>
        )}

        {kind === "article" && metaDescription && (
          <p className="text-xs text-muted-foreground" data-testid="meta-description">
            <span className="font-medium text-foreground/80">Search description: </span>
            {metaDescription}
          </p>
        )}

        {rejectedReason && (
          <p className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground/90" data-testid="rejection-reason">
            <span className="font-medium">You rejected this: </span>
            {rejectedReason}
          </p>
        )}

        {decision("top")}

        {rejecting && (
          <div className="space-y-2 rounded-xl border border-border bg-muted/30 p-3" data-testid="reject-panel">
            <label htmlFor="reject-reason" className="text-sm font-medium text-foreground">
              What should change? <span className="font-normal text-muted-foreground">(a short note helps the team)</span>
            </label>
            <Textarea
              id="reject-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="For example: the figure for 2027 is out of date, and the tone is too salesy."
              data-testid="input-reject-reason"
            />
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="destructive" disabled={reject.isPending} onClick={submitReject} data-testid="button-confirm-reject">
                {reject.isPending && <Loader2 size={14} className="animate-spin" />}
                Reject
              </Button>
              <Button type="button" variant="ghost" onClick={() => setRejecting(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {isApproved(item) && !published && !autoManaged && (
          <div className="border-t border-border pt-4">
            {kind === "article" || kind === "social" ? (
              <PublishActions item={item} workspace={workspace} />
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="note-document-approved">
                Approved. This is a working document, so there is nothing to publish from here.
              </p>
            )}
          </div>
        )}

        {published && (
          <div className="border-t border-border pt-3">
            <LiveLink item={item} />
          </div>
        )}
      </section>

      {/* Score card and independent QA: the evidence behind the checks, before the article itself */}
      <QualityReview key={item.id} item={item} />

      {/* Checks beside the article on wide screens; above it, collapsed, on narrow ones */}
      <div
        className={sideBySide ? "grid grid-cols-[minmax(0,1fr)_20rem] items-start gap-4" : "space-y-4"}
        data-testid="review-layout"
        data-layout={sideBySide ? "side-by-side" : "stacked"}
      >
        {!sideBySide && (
          <ChecksPanel item={item} workspace={workspace} sourcesCount={sourcesCount} hasSourcesPanel={hasSourcesPanel} notInForce={ledgerNotInForce} graded={graded} onSeeSources={seeSources} collapsible />
        )}
        <div ref={bodyRef} className="min-w-0">
          <BodyReader prepared={prepared} title={title} ledger={ledger} />
        </div>
        {sideBySide && (
          <aside className="custom-scrollbar sticky top-2 max-h-[calc(100vh-1rem)] overflow-y-auto" aria-label="Checks" data-testid="review-aside">
            <ChecksPanel item={item} workspace={workspace} sourcesCount={sourcesCount} hasSourcesPanel={hasSourcesPanel} notInForce={ledgerNotInForce} graded={graded} onSeeSources={seeSources} />
          </aside>
        )}
      </div>

      {/* Decision again, at the end of the reading */}
      <div className="flex justify-end border-t border-border pt-4">{decision("bottom")}</div>

      {editing && renderEditor(item, () => setEditing(false))}
    </div>
  );
}
